import {
  NavItemBlueprint,
  SubPageBlueprint,
  createExtensionInput,
  createFrontendModule,
  createFrontendPlugin,
} from '@backstage/frontend-plugin-api';
import {
  FormFieldBlueprint,
  formFieldsApiRef,
} from '@backstage/plugin-scaffolder-react/alpha';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';

/**
 * Replaces the stock `sub-page:scaffolder/templates` extension (disabled in
 * `app-config.yaml`) with a filtered template list + wizard. Attaches to
 * the upstream `page:scaffolder` so sibling sub-pages (`tasks`, `edit`, …)
 * keep working without deep-importing internal plugin modules.
 *
 * See `packages/app/src/components/scaffolder/AdminFilteredCreatePage.tsx`
 * for the filtering rationale and relationship to the backend
 * `auth:require-permission` guard on admin-only templates.
 */
const filteredTemplatesSubPage = SubPageBlueprint.makeWithOverrides({
  name: 'templates',
  inputs: {
    formFields: createExtensionInput([
      FormFieldBlueprint.dataRefs.formFieldLoader,
    ]),
  },
  factory(originalFactory, { apis }) {
    const formFieldsApi = apis.get(formFieldsApiRef);
    return originalFactory({
      path: 'templates',
      title: 'Templates',
      loader: async () => {
        const formFields = (await formFieldsApi?.loadFormFields()) ?? [];
        const { FilteredTemplatesSubPage } = await import(
          '../../components/scaffolder/AdminFilteredCreatePage'
        );
        return <FilteredTemplatesSubPage formFields={formFields} />;
      },
    });
  },
});

export const scaffolderTemplatesModule = createFrontendModule({
  pluginId: 'scaffolder',
  extensions: [filteredTemplatesSubPage],
});

/**
 * Sidebar nav item for `/create`. The upstream `nav-item:scaffolder` is
 * disabled in `app-config.yaml`; this replacement points at the same
 * `scaffolderPlugin.routes.root` routeRef so deep links keep resolving.
 *
 * Picked in `packages/app/src/modules/nav/Sidebar.tsx` via
 * `nav.take('nav-item:scaffolder-override')`.
 */
const scaffolderNavItem = NavItemBlueprint.make({
  params: {
    routeRef: scaffolderPlugin.routes.root,
    title: 'Create...',
    icon: CreateComponentIcon,
  },
});

/**
 * Custom Scaffolder field — `LoginThemePreview`. Renders a static
 * approximation of the Keycloak login theme (split-screen, light/
 * dark toggle, language switcher, SSO buttons) using the brand /
 * company / division values the user types into earlier fields of
 * the form, so admins can see what they're about to create BEFORE
 * the scaffold runs.
 *
 * The template that consumes this field lives in the
 * `backstage-template-project` repo (`keycloak-docker/template.yaml`).
 * It references the field via `ui:field: LoginThemePreview` on the
 * step that collects the branding inputs.
 *
 * The dynamic import keeps the preview component, its strings table
 * (4 locales) and its styles OUT of the main app bundle — they only
 * load when someone opens a Scaffolder template that asks for the
 * field.
 *
 * Naming note (same trick as `widget-gallery` in
 * `contentPagesModule`): the registry key `login-theme-preview`
 * here is internal to the Backstage extension system. What the
 * template YAML references is the `name` declared inside
 * `createFormField` ("LoginThemePreview"). Both are wired by the
 * `FormFieldBlueprint` below; keep them in sync mentally when
 * renaming.
 */
const loginThemePreviewFormField = FormFieldBlueprint.make({
  name: 'login-theme-preview',
  params: {
    field: () =>
      import('../../components/scaffolder/LoginThemePreview').then(
        m => m.LoginThemePreviewFormField,
      ),
  },
});

/**
 * Custom Scaffolder field — `TemplateIframePreview`.
 *
 * Generic alternative to `LoginThemePreview`: instead of hand-
 * coding a React component PER template inside the Backstage app,
 * the template author ships its OWN `preview/index.html`
 * (plus optional `preview/style.css`) inside its repo. This field
 * reads them via the `/api/template-preview/fetch` proxy (which
 * uses `UrlReader`, so the same Azure DevOps credential the
 * catalog uses applies) and renders the result inside a sandboxed
 * iframe with live `{{ key }}` substitution from the form data.
 *
 * Why we keep BOTH this generic field AND `LoginThemePreview`:
 *
 *   - `LoginThemePreview` is the bespoke, JSX-driven mock for the
 *     Keycloak login theme. It carries Backstage-side state (the
 *     dark/light toggle, the language switcher) that has to live
 *     in React. We keep it as-is for templates that NEED that
 *     interactivity AND for backwards-compat with the existing
 *     `keycloak-docker` template until the team migrates it.
 *
 *   - `TemplateIframePreview` is the path forward for every NEW
 *     template that just wants "show what I'll generate, with
 *     the user's brand strings substituted". Lower fidelity
 *     (HTML+CSS only, no React state) but zero per-template code
 *     inside this app.
 *
 * Both register cleanly side-by-side; templates pick whichever
 * fits via `ui:field: ...` in their YAML.
 *
 * The dynamic import (same pattern as `login-theme-preview` above)
 * keeps the iframe component + its types OUT of the main app
 * bundle — they only load when a template that uses this field
 * is opened in the wizard.
 */
const templateIframePreviewFormField = FormFieldBlueprint.make({
  name: 'template-iframe-preview',
  params: {
    field: () =>
      import('../../components/scaffolder/TemplateIframePreview').then(
        m => m.TemplateIframePreviewFormField,
      ),
  },
});

/**
 * Custom Scaffolder field — `AzureProjectPicker`.
 *
 * Populates a dropdown of Azure DevOps projects for a fixed organization
 * (`ui:options.organization`, e.g. BISB-DF). Values are proxied through
 * `/api/scaffolder-fields/azure/projects` so the integration PAT never
 * reaches the browser. Consumed by `keycloak-docker` and other templates
 * that need `projectName` before `publish:azure`.
 */
const azureProjectPickerFormField = FormFieldBlueprint.make({
  name: 'azure-project-picker',
  params: {
    field: () =>
      import('../../components/scaffolder/AzureProjectPicker').then(
        m => m.AzureProjectPickerFormField,
      ),
  },
});

/**
 * Overrides the stock `OwnerPicker` scaffolder field with a read-only
 * "Digital Factory" label. Templates that still reference
 * `ui:field: OwnerPicker` cannot assign squad or RBAC group owners.
 */
const fixedOwnerPickerFormField = FormFieldBlueprint.make({
  name: 'fixed-owner-picker',
  params: {
    field: () =>
      import('../../components/scaffolder/FixedOwnerPicker').then(
        m => m.FixedOwnerPickerFormField,
      ),
  },
});

/**
 * Hosts the sidebar nav item for `/create` and every custom Scaffolder
 * field the app contributes. The filtered templates sub-page lives in
 * `scaffolderTemplatesModule` (pluginId `scaffolder`) so it attaches
 * to the upstream page alongside `tasks`, `edit`, etc.
 *
 * `pluginId: 'scaffolder-override'` avoids colliding with the upstream
 * `scaffolder` pluginId for form-field extensions.
 */
export const scaffolderModule = createFrontendPlugin({
  pluginId: 'scaffolder-override',
  extensions: [
    scaffolderNavItem,
    loginThemePreviewFormField,
    templateIframePreviewFormField,
    azureProjectPickerFormField,
    fixedOwnerPickerFormField,
  ],
});
